/** SUNTRONIX ONE — Product Sync & Coverage Control Centre.
 *  Technology → Product Content → Sync & Coverage. Live catalogue readiness,
 *  image/part-number/colour/HSN coverage, per-brand rollup and a governed
 *  one-click Complete Catalogue. All figures are live from the Product Master.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Gauge, ImageIcon, Hash, Palette, Receipt, CheckCircle2, AlertTriangle,
  Loader2, Sparkles, RefreshCw, PackageCheck, ImageOff, FileText, Download, ShieldAlert,
} from "lucide-react";

const GO_LIVE = {
  READY: { t: "Catalogue READY", c: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  READY_WITH_EXCEPTIONS: { t: "Ready with exceptions", c: "bg-amber-100 text-amber-800 border-amber-200" },
  NOT_READY: { t: "Not ready", c: "bg-rose-100 text-rose-700 border-rose-200" },
};

function Card({ icon: Icon, label, value, sub, tone, onClick, testid }) {
  return (
    <button onClick={onClick} disabled={!onClick} data-testid={testid}
      className={"text-left rounded-xl border border-slate-200 bg-white p-4 " +
        (onClick ? "hover:border-slate-400 hover:shadow-sm transition-all cursor-pointer" : "cursor-default")}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-slate-500">
        <span className={"h-6 w-6 rounded-md grid place-items-center " + (tone || "bg-slate-100 text-slate-600")}>
          <Icon className="h-3.5 w-3.5" />
        </span>{label}
      </div>
      <div className="text-[22px] font-bold text-slate-900 mt-1.5 tabular-nums">{value ?? "—"}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </button>
  );
}

export default function CoverageCentre() {
  const nav = useNavigate();
  const [ov, setOv] = useState(null);
  const [brands, setBrands] = useState([]);
  const [jobs, setJobs] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  const [busy, setBusy] = useState(false);
  const poll = useRef(null);

  const load = useCallback(() => {
    Promise.all([
      api.get("/coverage/overview"),
      api.get("/coverage/by-brand"),
      api.get("/coverage/jobs").catch(() => ({ data: null })),
      api.get("/coverage/conflicts", { params: { page_size: 1 } }).catch(() => ({ data: null })),
    ]).then(([o, b, j, c]) => { setOv(o.data); setBrands(b.data.items || []); setJobs(j.data); setConflicts(c.data); })
      .catch(() => toast.error("Failed to load coverage"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const dl = (fmt) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/coverage/export.${fmt}`, "_blank");

  const anyRunning = jobs && [jobs.image, jobs.part_number, jobs.colour, jobs.description]
    .some((x) => x && (x.status === "RUNNING"));
  useEffect(() => {
    if (anyRunning) {
      poll.current = setInterval(load, 2500);
      return () => clearInterval(poll.current);
    }
  }, [anyRunning, load]);

  const completeCatalogue = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/coverage/complete-catalogue");
      toast.success("Complete Catalogue started — part numbers → colours → images");
      setTimeout(load, 1500);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
    finally { setBusy(false); }
  };

  const gl = GO_LIVE[ov?.go_live_status] || GO_LIVE.NOT_READY;

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-5" data-testid="coverage-centre-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-slate-900 grid place-items-center text-white"><PackageCheck className="h-5 w-5" /></div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Sync &amp; Coverage</h1>
            <p className="text-[13px] text-slate-500">Live catalogue readiness across image, part number, colour and HSN — every figure from the Product Master.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={"text-[11px] font-semibold px-2.5 py-1 rounded-full border " + gl.c} data-testid="coverage-golive">{gl.t}</span>
          <Button variant="outline" onClick={() => dl("xlsx")} data-testid="coverage-export-xlsx" title="Export XLSX"><Download className="h-4 w-4 mr-1" />XLSX</Button>
          <Button variant="outline" onClick={() => dl("pdf")} data-testid="coverage-export-pdf" title="Export PDF"><FileText className="h-4 w-4 mr-1" />PDF</Button>
          <Button variant="outline" onClick={load} data-testid="coverage-refresh"><RefreshCw className="h-4 w-4" /></Button>
          <Button onClick={completeCatalogue} disabled={busy || anyRunning} data-testid="coverage-complete"
            className="bg-slate-900 hover:bg-slate-800 text-white">
            {busy || anyRunning ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
            Complete Catalogue
          </Button>
        </div>
      </div>

      {/* Readiness banner */}
      {ov && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="coverage-readiness">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Catalogue Readiness (quote-ready)</div>
              <div className="text-4xl font-bold text-slate-900 tabular-nums">{ov.catalogue_readiness_pct}%</div>
              <div className="text-[12px] text-slate-500 mt-1">{ov.quote_ready?.toLocaleString()} of {ov.total?.toLocaleString()} products quote-ready · {ov.fully_synced?.toLocaleString()} fully synced</div>
            </div>
            <div className="text-right text-[12px] text-slate-500">
              <div>Visual coverage <b className="text-emerald-700">{ov.visual_coverage_pct}%</b></div>
              <div>Exact official images <b className="text-sky-700">{ov.exact_image_pct}%</b></div>
              <div>Placeholders <b className="text-slate-600">{ov.placeholder_pct}%</b></div>
            </div>
          </div>
          <div className="mt-3 h-2.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-emerald-500" style={{ width: `${ov.catalogue_readiness_pct}%` }} />
          </div>
        </div>
      )}

      {/* Coverage cards (drill-down) */}
      {ov && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="coverage-cards">
          <Card testid="cc-total" icon={PackageCheck} label="Total Products" value={ov.total?.toLocaleString()} tone="bg-slate-900 text-white" />
          <Card testid="cc-quote" icon={CheckCircle2} label="Quote Ready" value={ov.quote_ready?.toLocaleString()} tone="bg-emerald-100 text-emerald-700" />
          <Card testid="cc-exact" icon={ImageIcon} label="Exact Image" value={ov.exact_image?.toLocaleString()} sub={`${ov.exact_image_pct}%`} tone="bg-sky-100 text-sky-700"
            onClick={() => nav("/technology/image-enrichment")} />
          <Card testid="cc-placeholder" icon={ImageIcon} label="Placeholder" value={ov.placeholder?.toLocaleString()} tone="bg-slate-100 text-slate-600"
            onClick={() => nav("/technology/image-enrichment")} />
          <Card testid="cc-missing-img" icon={ImageOff} label="Missing Image" value={ov.missing_image?.toLocaleString()} tone="bg-rose-100 text-rose-700"
            onClick={() => nav("/technology/image-enrichment")} />
          <Card testid="cc-pn" icon={Hash} label="Part # Missing" value={(ov.part_number_missing + ov.part_number_internal_misused)?.toLocaleString()} tone="bg-amber-100 text-amber-700"
            onClick={() => nav("/technology/part-number-enrichment")} />
          <Card testid="cc-colour" icon={Palette} label="Colour Missing" value={ov.colour_missing?.toLocaleString()} tone="bg-fuchsia-100 text-fuchsia-700"
            onClick={() => nav("/technology/colour-enrichment")} />
          <Card testid="cc-hsn" icon={Receipt} label="HSN Missing" value={ov.hsn_missing?.toLocaleString()} tone="bg-indigo-100 text-indigo-700"
            onClick={() => nav("/technology/hsn-enrichment")} />
          <Card testid="cc-desc" icon={FileText} label="Description Missing" value={ov.description_missing?.toLocaleString()} tone="bg-teal-100 text-teal-700"
            onClick={() => nav("/technology/description-enrichment")} />
          <Card testid="cc-desc-ai" icon={FileText} label="AI Descriptions" value={ov.description_ai_generated?.toLocaleString()} tone="bg-sky-100 text-sky-700"
            onClick={() => nav("/technology/description-enrichment")} />
          <Card testid="cc-review" icon={AlertTriangle} label="Review Required" value={ov.review_required?.toLocaleString()} tone="bg-amber-100 text-amber-700" />
          <Card testid="cc-conflicts" icon={ShieldAlert} label="Spec Conflicts" value={conflicts?.total?.toLocaleString() ?? "—"} tone="bg-rose-100 text-rose-700"
            onClick={() => nav("/technology/spec-conflicts")} />
          <Card testid="cc-mismatch" icon={AlertTriangle} label="Colour/Image Conflict" value={ov.colour_image_mismatch?.toLocaleString()} tone="bg-rose-100 text-rose-700" />
          <Card testid="cc-fully" icon={CheckCircle2} label="Fully Synced" value={ov.fully_synced?.toLocaleString()} tone="bg-emerald-100 text-emerald-700" />
          <Card testid="cc-notready" icon={AlertTriangle} label="Not Quote Ready" value={ov.not_quote_ready?.toLocaleString()} tone="bg-slate-100 text-slate-500" />
        </div>
      )}

      {/* Running jobs */}
      {anyRunning && (
        <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-4 text-[12px] text-slate-700" data-testid="coverage-jobs">
          <div className="font-semibold mb-1 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin text-sky-600" /> Enrichment running…</div>
          {["part_number", "colour", "image", "description"].map((k) => {
            const j = jobs?.[k]; if (!j) return null;
            const p = j.processed || 0, t = j.total || 0;
            return <div key={k} className="mt-0.5">{k.replace("_", " ")}: {j.status} · {p}/{t}</div>;
          })}
        </div>
      )}

      {/* Brand coverage */}
      <div>
        <h3 className="text-[13px] font-semibold text-slate-700 mb-2">Brand coverage</h3>
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="coverage-brand-table">
            <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
              {["Brand", "Total", "Exact Image", "Placeholder", "Part# Missing", "Colour Missing", "HSN Missing", "Readiness"].map((h, i) => <th key={i} className="px-3 py-2 font-semibold">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {brands.map((b) => (
                <tr key={b.brand} className="hover:bg-slate-50" data-testid={`coverage-brand-${b.brand}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{b.brand}</td>
                  <td className="px-3 py-2 tabular-nums">{b.total}</td>
                  <td className="px-3 py-2 tabular-nums text-sky-700">{b.exact_image}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-500">{b.placeholder}</td>
                  <td className="px-3 py-2 tabular-nums text-amber-700">{b.part_number_missing}</td>
                  <td className="px-3 py-2 tabular-nums text-fuchsia-700">{b.colour_missing}</td>
                  <td className="px-3 py-2 tabular-nums text-indigo-700">{b.hsn_missing}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className={"h-full " + (b.readiness_pct >= 95 ? "bg-emerald-500" : b.readiness_pct >= 80 ? "bg-amber-500" : "bg-rose-500")}
                          style={{ width: `${b.readiness_pct}%` }} />
                      </div>
                      <span className="tabular-nums text-[11px] text-slate-600">{b.readiness_pct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
